import { Input } from 'almanac-ui/TextInputField/Input'
import { InputProps } from 'almanac-ui/TextInputField/Types'
import InputStyles from 'almanac-ui/TextInputField/Input.module.scss'
import cx from 'classnames'
import { useRef, useState, KeyboardEvent, forwardRef, useImperativeHandle } from 'react'
import { SnippetsToolbar } from '../SnippetsToolbar/SnippetsToolbar'
import Styles from './SnippetsInput.module.scss'
import { InputSize } from 'almanac-ui/TextInputField/Types'
import { KnowledgeBaseSnippetKind } from 'types'
import { Pressable, useFocusWithin } from '@react-aria/interactions'
import { Plugin } from 'prosemirror-state'
import { Button } from 'blaze-ui/Button'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

export type SnippetsInputHandle = {
  focus: () => void
}

export interface SnippetsInputProps
  extends Omit<InputProps, 'children' | 'onChange' | 'onBlur' | 'onFocus' | 'plugins' | 'onKeyDown'> {
  onChange: (value: string) => void
  name: string
  value: string
  isValid?: boolean
  isTextarea?: boolean
  size?: InputSize
  kinds?: KnowledgeBaseSnippetKind[]
  hideSnippets?: boolean
  plugins?: Array<Plugin>
  typeaheadText?: string
  acceptTypeahead?: () => void
  cancelTypeahead?: () => void
  onKeyDown?: (event: KeyboardEvent) => void
}

export const SnippetsInput = forwardRef<SnippetsInputHandle, SnippetsInputProps>(
  (
    {
      onChange,
      isTextarea,
      value,
      isValid = true,
      size = 'md',
      kinds,
      hideSnippets,
      plugins = [],
      typeaheadText,
      acceptTypeahead,
      cancelTypeahead,
      onKeyDown,
      variant = '',
      ...rest
    },
    fRef,
  ) => {
    const [isFocused, setIsFocused] = useState(rest.autoFocus ?? false)
    const scrollRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null)
    const { focusWithinProps } = useFocusWithin({ onFocusWithinChange: setIsFocused })
    const { isMobile } = useResponsiveDesign()
    const workspace = useCurrentWorkspace()

    const onInsertSnippet = (snippet: string) => {
      if (!inputRef.current) return
      const start = inputRef.current.selectionStart ?? inputRef.current.value.length
      const end = inputRef.current.selectionEnd ?? inputRef.current.value.length
      const newValue = value.slice(0, start) + snippet + value.slice(end)
      onChange(newValue)
      inputRef.current.focus()
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
      }, 0)
    }

    useImperativeHandle(fRef, () => ({
      focus: () => {
        inputRef.current?.focus()
      },
    }))

    const handleKeyDown = (e: KeyboardEvent) => {
      onKeyDown?.(e)
      if (!typeaheadText) return

      switch (e.key) {
        case 'Tab':
        case 'Enter':
          if (!acceptTypeahead) return

          e.preventDefault()
          acceptTypeahead()
          inputRef.current?.focus()
          setTimeout(() => {
            scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
          }, 0)
          break
        case 'Escape':
          if (!cancelTypeahead) return

          e.preventDefault()
          e.stopPropagation()
          cancelTypeahead()
          break
        default:
          break
      }
    }

    return (
      <div
        onClick={() => {
          if (inputRef.current && document?.activeElement !== inputRef.current) {
            inputRef.current?.focus()
          }
        }}
        className={cx(
          rest.className,
          Styles.wrap,
          InputStyles[size],
          InputStyles[variant],
          InputStyles.input,
          InputStyles.withBorder,
          {
            [InputStyles.invalid]: !isValid,
            [InputStyles.textarea]: isTextarea,
            [InputStyles.focused]: isFocused,
            [Styles.focused]: isFocused,
            [Styles.isTextarea]: isTextarea,
          },
        )}
        {...focusWithinProps}
      >
        <div ref={scrollRef} className={Styles.scrollContainer}>
          <div className={Styles.inputTypeaheadContainer}>
            <Input
              {...rest}
              ref={inputRef}
              className={cx(Styles.input, InputStyles[size])}
              isValid={isValid}
              isTextarea={isTextarea}
              size={size}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => cancelTypeahead?.()}
              withBorder={false}
            />
            {typeaheadText && (
              <Pressable onPress={acceptTypeahead}>
                <div className={cx(Styles.typeaheadText, Styles.input, InputStyles[size])}>
                  <span>{value + typeaheadText}</span>
                  <div className={Styles.tabIndicator}>
                    <Button size="xs" variant="tertiary" className={Styles.tabButton}>
                      {isMobile ? 'Tap' : 'Tab'} to accept
                    </Button>
                  </div>
                </div>
              </Pressable>
            )}
          </div>
        </div>
        {workspace?.snippets_enabled && !hideSnippets && (
          <SnippetsToolbar
            inputValue={value}
            onInsert={onInsertSnippet}
            kinds={kinds}
            shown={isFocused || isMobile}
            focusInput={() => inputRef.current?.focus()}
          />
        )}
      </div>
    )
  },
)
